import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { OC, FONT, STATUS_META, CATEGORY } from '../constants/index.js';
import { useTheme } from '../constants/index.js';
import { useStore } from '../store/StoreProvider.js';
import OCApi from '../api/client.js';
import { Button, Badge, Icon, Card, MonoTag, CategoryIcon } from '../components/ui.js';
import { EmptyState } from '../components/AsyncState.js';

// --- ApplicationsScreen ---
function ApplicationsScreen() {
    const s = useStore();
    const t = useTheme(s.theme);
    const [tab, setTab] = useState('all');

    useEffect(() => {
        if (!s.authReady) return;
        if (!s.authed) {
            s.showToast('Log in to track applications', 'briefcase');
            s.go('login');
            return;
        }
        s.loadApplicationsFromApi();
    }, [s.authReady, s.authed]);

    const entries = s.applied.map(entry => {
        const op = s.findOpportunity(entry.id);
        return {
            ...entry,
            title: op?.title || entry.title || 'Untitled',
            org: op?.org || entry.org || 'Unknown',
            category: op?.category || entry.category || 'jobs',
            location: op?.location || entry.location || '',
            isExpired: op?.isExpired || false,
        };
    });

    const activeStatuses = ['applied', 'review', 'interview'];
    const closedStatuses = ['rejected', 'withdrawn', 'closed'];

    const filtered = tab === 'all' ? entries
        : tab === 'active' ? entries.filter(e => activeStatuses.includes(e.status))
        : tab === 'offers' ? entries.filter(e => e.status === 'offer')
        : entries.filter(e => closedStatuses.includes(e.status));

    const statCounts = {
        applied: entries.filter(e => e.status === 'applied').length,
        review: entries.filter(e => e.status === 'review').length,
        interview: entries.filter(e => e.status === 'interview').length,
        offer: entries.filter(e => e.status === 'offer').length,
    };

    const tabs = [
        ['all', 'All', entries.length],
        ['active', 'Active', entries.filter(e => activeStatuses.includes(e.status)).length],
        ['offers', 'Offers', statCounts.offer],
        ['closed', 'Closed', entries.filter(e => closedStatuses.includes(e.status)).length],
    ];

    const statusOptions = ['applied', 'review', 'interview', 'offer', 'rejected', 'withdrawn', 'closed'];

    return React.createElement('div', { style: { maxWidth: 1180, margin: '0 auto', padding: '0 32px', width: '100%' } },
        React.createElement('div', { style: { padding: '40px 0 80px' } },
            React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 24, flexWrap: 'wrap', marginBottom: 22 } },
                React.createElement('div', null,
                    React.createElement('h1', { style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 34, margin: 0, color: t.text.primary, letterSpacing: '-0.025em' } },
                        'Application tracker'
                    ),
                    React.createElement('p', { style: { fontFamily: FONT.body, fontSize: 15, color: t.text.secondary, margin: '6px 0 0' } },
                        React.createElement('strong', { style: { color: t.text.primary } }, entries.length),
                        ' tracked',
                        statCounts.interview ? React.createElement(React.Fragment, null, ' · ', React.createElement('span', { style: { color: OC.warning, fontWeight: 600 } }, statCounts.interview, ' interview(s)')) : null
                    )
                ),
                React.createElement(Button, { kind: 'secondary', size: 'md', theme: s.theme, onClick: () => s.go('feed'), icon: React.createElement(Icon, { name: 'plus', size: 15 }) }, 'Browse opportunities')
            ),
            entries.length ? [
                React.createElement('div', {
                    key: 'stats',
                    style: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }
                },
                    [
                        ['Applied', statCounts.applied, '#3B82F6'],
                        ['In review', statCounts.review, '#8B5CF6'],
                        ['Interview', statCounts.interview, '#F59E0B'],
                        ['Offers', statCounts.offer, '#22C55E'],
                    ].map(([label, value, color]) =>
                        React.createElement('div', {
                            key: label,
                            style: {
                                background: t.bg.card, border: `1px solid ${t.border.base}`,
                                borderRadius: 14, padding: '16px 18px', textAlign: 'center',
                            }
                        },
                            React.createElement('div', { style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 24, color } }, value),
                            React.createElement('div', { style: { fontSize: 12.5, color: t.text.secondary, marginTop: 4 } }, label)
                        )
                    )
                ),
                React.createElement('div', { key: 'tabs', style: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 22 } },
                    tabs.map(([k, label, n]) => {
                        const active = tab === k;
                        return React.createElement('button', {
                            key: k, onClick: () => setTab(k),
                            style: {
                                display: 'inline-flex', alignItems: 'center', gap: 7,
                                border: `1px solid ${active ? OC.orange : t.border.base}`,
                                background: active ? OC.orange : t.bg.card,
                                color: active ? '#fff' : t.text.secondary,
                                borderRadius: 999, padding: '9px 15px',
                                cursor: 'pointer', fontFamily: FONT.body,
                                fontWeight: 600, fontSize: 13.5,
                            }
                        }, label,
                            React.createElement('span', {
                                style: {
                                    minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9,
                                    background: active ? 'rgba(255,255,255,.25)' : t.bg.subtle,
                                    color: active ? '#fff' : t.text.secondary,
                                    fontSize: 11, fontWeight: 700,
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                }
                            }, n)
                        );
                    })
                ),
                React.createElement('div', { key: 'list', style: { display: 'flex', flexDirection: 'column', gap: 12 } },
                    filtered.map(entry => {
                        const meta = STATUS_META[entry.status] || STATUS_META.applied;
                        const cat = CATEGORY[entry.category] || CATEGORY.jobs;
                        return React.createElement('div', {
                            key: entry.applicationId || entry.id,
                            style: {
                                background: t.bg.card, border: `1px solid ${t.border.base}`,
                                borderRadius: 16, padding: '18px 20px',
                                display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, alignItems: 'center',
                            }
                        },
                            React.createElement('div', null,
                                React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' } },
                                    React.createElement(Badge, { color: cat.color, theme: s.theme, icon: React.createElement(CategoryIcon, { category: entry.category, size: 12, theme: s.theme }) }, cat.label.slice(0, -1)),
                                    React.createElement('span', {
                                        style: {
                                            fontSize: 11.5, fontWeight: 700, color: meta.color,
                                            background: `${meta.color}18`, padding: '4px 10px', borderRadius: 999,
                                        }
                                    }, meta.label),
                                    entry.isExpired ? React.createElement('span', {
                                        style: {
                                            fontSize: 11.5, fontWeight: 700, color: OC.neutral,
                                            background: `${OC.neutral}18`, padding: '4px 10px', borderRadius: 999,
                                        }
                                    }, 'Expired') : null
                                ),
                                React.createElement('button', {
                                    onClick: () => s.go('detail', { id: entry.id }),
                                    style: {
                                        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                                        textAlign: 'left', fontFamily: FONT.display, fontWeight: 800,
                                        fontSize: 18, color: t.text.primary, letterSpacing: '-0.01em',
                                    }
                                }, entry.title),
                                React.createElement('div', { style: { fontSize: 13.5, color: t.text.secondary, marginTop: 4 } },
                                    [entry.org, entry.location, entry.date ? `Applied ${entry.date}` : null].filter(Boolean).join(' · ')
                                ),
                                entry.applyChannel === 'internal' ? React.createElement('div', { style: { fontSize: 12, color: t.text.secondary, marginTop: 6 } }, 'Submitted on Opportunity Circle') : null,
                                entry.coverLetter ? React.createElement('div', { style: { fontSize: 12.5, color: t.text.secondary, marginTop: 8, lineHeight: 1.45 } }, entry.coverLetter) : null
                            ),
                            React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 8, minWidth: 180 } },
                                entry.resumeUrl && entry.applicationId ? React.createElement(Button, {
                                    kind: 'secondary', size: 'sm', theme: s.theme,
                                    onClick: () => OCApi.downloadApplicationResume(entry.applicationId),
                                    icon: React.createElement(Icon, { name: 'download', size: 14 }),
                                }, 'Download CV') : null,
                                React.createElement('select', {
                                    value: entry.status,
                                    onChange: e => s.setApplyStatus(entry.id, e.target.value),
                                    style: {
                                        border: `1px solid ${t.border.base}`, borderRadius: 10,
                                        padding: '9px 12px', background: t.bg.page, color: t.text.primary,
                                        fontFamily: FONT.body, fontSize: 13, fontWeight: 600,
                                    }
                                }, statusOptions.map(st =>
                                    React.createElement('option', { key: st, value: st }, (STATUS_META[st] || STATUS_META.applied).label)
                                )),
                                React.createElement(Button, {
                                    kind: 'ghost', size: 'sm', theme: s.theme,
                                    onClick: () => s.removeApplication(entry.id),
                                }, 'Remove')
                            )
                        );
                    })
                )
            ] : React.createElement('div', { style: { textAlign: 'center', padding: '64px 24px', border: `1px dashed ${t.border.strong}`, borderRadius: 18, background: t.bg.card } },
                React.createElement('h3', { style: { fontFamily: FONT.display, fontWeight: 700, fontSize: 19, color: t.text.primary, margin: '0 0 6px' } },
                    'No applications tracked yet'
                ),
                React.createElement('p', { style: { fontFamily: FONT.body, fontSize: 14.5, color: t.text.secondary, margin: '0 0 20px' } },
                    'Apply to an opportunity, then confirm with “I applied” to start tracking progress here.'
                ),
                React.createElement(Button, { kind: 'primary', size: 'md', theme: s.theme, onClick: () => s.go('feed') }, 'Browse opportunities')
            )
        )
    );
}

// --- EMPLOYER_STATUS_META ---
const EMPLOYER_STATUS_META = {
    pending: { label: 'Pending review', color: OC.warning },
    live: { label: 'Live', color: OC.success },
    closed: { label: 'Closed', color: OC.neutral },
};

// --- EmployerJobRow ---
function EmployerJobRow({ job }) {
    const s = useStore();
    const t = useTheme(s.theme);
    const cat = CATEGORY[job.category] || CATEGORY.jobs;
    const meta = EMPLOYER_STATUS_META[job.status] || EMPLOYER_STATUS_META.pending;

    return React.createElement(Card, { theme: s.theme, padding: 20 },
        React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' } },
            React.createElement('div', { style: { display: 'flex', gap: 14, alignItems: 'flex-start', minWidth: 0, flex: 1 } },
                React.createElement(CategoryIcon, { category: job.category, size: 38, theme: s.theme }),
                React.createElement('div', { style: { minWidth: 0 } },
                    React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' } },
                        React.createElement(Badge, { color: meta.color, theme: s.theme }, meta.label),
                        React.createElement(Badge, { color: cat.color, theme: s.theme }, cat.label),
                        React.createElement('span', { style: { fontSize: 12, color: t.text.secondary } }, job.postedAt)
                    ),
                    React.createElement('div', { style: { fontFamily: FONT.display, fontWeight: 700, fontSize: 16, color: t.text.primary } }, job.title),
                    React.createElement('div', { style: { fontSize: 13, color: t.text.secondary, marginTop: 2 } }, job.org, ' · ', job.location)
                )
            ),
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, position: 'relative' } },
                React.createElement('div', { style: { textAlign: 'right', marginRight: 4 } },
                    React.createElement('div', { style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 18, color: t.text.primary } }, job.applicants ?? job.applicationCount ?? 0),
                    React.createElement('div', { style: { fontSize: 11.5, color: t.text.secondary } }, 'applicants')
                )
            )
        )
    );
}

export { ApplicationsScreen, EmployerJobRow, EMPLOYER_STATUS_META };
